import type { ActiveJobDto } from '@chat-emp/shared';
import type { JobCard } from '../fixtures/domain.js';

/** Title or any location, case-insensitive; a blank query keeps every job. */
export function matchesActiveJob(job: ActiveJobDto, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;
  return [job.title, job.location ?? ''].some((text) => text.toLowerCase().includes(needle));
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/* The composer stores the id as a string; a card relayed through the backend arrives as the
   number its id decode rewrote it to. Either is the same id. */
function idText(value: unknown): string | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : text(value);
}

function strings(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.filter((item): item is string => typeof item === 'string' && item !== '');
  return items.length > 0 ? items : undefined;
}

/** http(s) only — a `javascript:` value from Stream must never reach an href. */
function webUrl(value: unknown): string | undefined {
  const url = text(value);
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}

// The card head's company, read defensively; logo kept only when http(s), dropped otherwise.
function jobCompanyOf(value: unknown): JobCard['company'] {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const name = text(raw.name);
  if (!name) return undefined;
  const logoUrl = webUrl(raw.logo_url);
  return {
    name,
    ...(logoUrl ? { logoUrl } : {}),
    ...(raw.verified === true ? { verified: true } : {}),
  };
}

/* Read the `job` field into a JobCard, defensively like `toFormNotice`; `url` kept only when http(s). */
export function toJobCard(value: unknown): JobCard | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const raw = value as Record<string, unknown>;
  const jobId = idText(raw.job_id);
  const title = text(raw.title);
  const jobType = text(raw.job_type);
  if (!jobId || !title || !jobType) return undefined;
  const location = text(raw.location);
  const salary = text(raw.salary);
  const description = text(raw.description);
  const responsibilities = strings(raw.responsibilities);
  const requirements = strings(raw.requirements);
  const url = webUrl(raw.url);
  const company = jobCompanyOf(raw.company);
  return {
    jobId,
    title,
    jobType,
    ...(location ? { location } : {}),
    ...(salary ? { salary } : {}),
    ...(description ? { description } : {}),
    ...(responsibilities ? { responsibilities } : {}),
    ...(requirements ? { requirements } : {}),
    ...(url ? { url } : {}),
    ...(company ? { company } : {}),
  };
}
